import { Link, useNavigate } from "react-router-dom";
import React, { useState } from "react";

import AuthService from "../services/auth.service";
import OrderService from "../services/order.service";
import toast from "react-hot-toast";
import { useCart } from "../context/CartContext";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../common/utils";

const money = (n) => formatCurrency(Number(n || 0));

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, resetCart } = useCart();
  const user = AuthService.getCurrentUser();

  const [form, setForm] = useState({
    customer_name: user ? user.username || "" : "",
    phone: "",
    shipping_address: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    t("checkout.errors.generic");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.customer_name.trim()) return toast.error(t("checkout.errors.nameRequired"));
    if (!form.phone.trim()) return toast.error(t("checkout.errors.phoneRequired"));
    if (!form.shipping_address.trim()) return toast.error(t("checkout.errors.addressRequired"));

    setSubmitting(true);
    OrderService.checkout({
      customer_name: form.customer_name.trim(),
      phone: form.phone.trim(),
      shipping_address: form.shipping_address.trim(),
      note: form.note.trim(),
    })
      .then((res) => {
        resetCart();
        toast.success(t("checkout.orderPlaced"));
        navigate(`/orders/${res.data.id}`);
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setSubmitting(false));
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="container mt-4">
        <h3>{t("checkout.title")}</h3>
        <p className="text-muted">{t("checkout.empty")}</p>
        <Link to="/catalog" className="btn btn-primary">{t("checkout.browseProducts")}</Link>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">{t("checkout.title")}</h3>

      <div className="row">
        {/* Shipping form */}
        <div className="col-md-7 mb-4">
          <form onSubmit={handleSubmit} className="card card-body">
            <h5 className="mb-3">{t("checkout.shippingDetails")}</h5>

            <div className="mb-3">
              <label className="form-label">{t("checkout.fullName")}</label>
              <input type="text" className="form-control" name="customer_name" value={form.customer_name} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label className="form-label">{t("checkout.phone")}</label>
              <input type="text" className="form-control" name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label className="form-label">{t("checkout.shippingAddress")}</label>
              <textarea className="form-control" rows={3} name="shipping_address" value={form.shipping_address} onChange={handleChange} />
            </div>
            <div className="mb-3">
              <label className="form-label">{t("checkout.noteOptional")}</label>
              <textarea className="form-control" rows={2} name="note" value={form.note} onChange={handleChange} />
            </div>

            <button type="submit" className="btn btn-primary btn-md" disabled={submitting}>
              {submitting ? t("checkout.placingOrder") : t("checkout.placeOrder")}
            </button>
          </form>
        </div>

        {/* Order summary */}
        <div className="col-md-5">
          <div className="card card-body">
            <h5 className="mb-3">{t("checkout.orderSummary")}</h5>
            <ul className="list-group list-group-flush mb-3">
              {cart.items.map((it) => (
                <li key={it.id} className="list-group-item d-flex justify-content-between px-0">
                  <span>{it.name} <span className="text-muted">× {it.quantity}</span></span>
                  <span>{money(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <div className="d-flex justify-content-between fw-bold fs-5">
              <span>{t("checkout.total")}</span>
              <span className="text-primary">{money(cart.total_amount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
